/**
 * Les comptes qui portent de l'argent au crédit d'une famille.
 *
 * ## Pourquoi cette file existe avant d'être pleine
 *
 * Mesure du 10 septembre 2026 : **17 familles portent 1 071,90 EUR de
 * provision, et une seule porte aussi une dette — 0,50 EUR à ré-affecter.**
 *
 * C'est minuscule, et c'est précisément le moment de la construire. La règle
 * D1 dit qu'une annulation émet une note de crédit ; quand la facture avait
 * déjà reçu de l'argent, cet argent **part en provision** au lieu d'être
 * remboursé. Chaque annulation d'une facture payée alimentera donc cette file.
 *
 * Mieux vaut que l'écran sache la montrer avant qu'elle n'existe : une file
 * qu'on découvre le jour où elle compte deux cents lignes est une file qu'on
 * ne sait pas lire.
 *
 * ## Ce que la vue montre, et ce qu'elle ne compte pas
 *
 * Toutes les familles à provision, pas seulement celles qui ont aussi une
 * dette : savoir qu'une famille porte 200 EUR au crédit et ne doit rien est
 * une réponse utile, et c'est la moitié du travail de ré-affectation.
 *
 * Les factures **annulées** ne comptent pas comme dette. Y verser une
 * provision reviendrait à payer une facture qu'on vient de décider de ne plus
 * réclamer.
 *
 * ## Ce que cet écran ne fait PAS
 *
 * Il ne ré-affecte pas. Le geste — déplacer une ligne de `payment_allocations`
 * de `provision` vers `invoice` — appartient à l'écran de rapprochement, qui
 * sait déjà défaire et refaire une affectation. Ici on montre **où il y a
 * quelque chose à faire** ; c'est ce qui manquait, pas l'outil.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, Loader2, PiggyBank, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { chargerProvisions } from '../../../lib/factures/gateway.supabase';
import { etiquetteFamille, type LigneProvision } from '../../../lib/factures/types';
import { dateCourte, euros } from './money';
import { CHEMINS } from '../../../lib/navigation/chemins';

export default function FileProvisions() {
  const [lignes, setLignes] = useState<LigneProvision[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      setLignes(await chargerProvisions());
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture des provisions impossible'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  const totalProvision = lignes.reduce((s, l) => s + l.provisionCents, 0);
  const aReaffecter = lignes.reduce((s, l) => s + l.aReaffecterCents, 0);
  const avecLesDeux = lignes.filter((l) => l.aReaffecterCents > 0).length;

  return (
    <div className="rounded-xl bg-white shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <PiggyBank className="h-5 w-5 text-indigo-600" />
            Provisions au crédit des familles
          </h2>
          <p className="mt-0.5 text-sm text-gray-600">
            L’argent reçu qui n’est affecté à aucune facture. Une note de crédit sur une
            facture déjà payée l’alimente : la note efface la dette, l’argent reste à la
            famille.
          </p>
        </div>
        <button
          type="button"
          onClick={charger}
          disabled={chargement}
          className="btn-outline flex items-center"
        >
          <RefreshCw className={clsx('mr-2 h-4 w-4', chargement && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {chargement ? (
        <div className="flex items-center gap-2 px-6 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture des provisions…
        </div>
      ) : erreur ? (
        <p className="m-6 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {erreur}
        </p>
      ) : lignes.length === 0 ? (
        <p className="px-6 py-8 text-sm text-gray-600">
          Aucune provision en cours. Tout l’argent reçu est affecté à une facture ou
          classé hors périmètre.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-1 gap-4 border-b border-gray-200 px-6 py-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">Familles concernées</dt>
              <dd className="text-xl font-semibold tabular-nums text-gray-900">{lignes.length}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">Total en provision</dt>
              <dd className="text-xl font-semibold tabular-nums text-gray-900">{euros(totalProvision)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-gray-500">
                À ré-affecter tout de suite
              </dt>
              <dd className="text-xl font-semibold tabular-nums text-gray-900">
                {euros(aReaffecter)}
              </dd>
              <dd className="text-xs text-gray-500">
                {avecLesDeux === 0
                  ? 'aucune famille ne porte à la fois une dette et une provision'
                  : `${avecLesDeux} famille${avecLesDeux > 1 ? 's' : ''} porte${avecLesDeux > 1 ? 'nt' : ''} les deux`}
              </dd>
            </div>
          </dl>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-6 py-3">Famille</th>
                  <th className="px-6 py-3 text-right">Provision</th>
                  <th className="px-6 py-3 text-right">Dette ouverte</th>
                  <th className="px-6 py-3 text-right">À ré-affecter</th>
                  <th className="px-6 py-3">Dernier mouvement</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {lignes.map((l) => (
                  <tr
                    key={l.userId}
                    className={clsx(
                      'hover:bg-gray-50',
                      /* Ce qui demande un geste se voit tout de suite. */
                      l.aReaffecterCents > 0 && 'border-l-4 border-indigo-500',
                    )}
                  >
                    <td className="px-6 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {etiquetteFamille(l.nom, l.prenom)}
                      </div>
                      {l.courriel && <div className="text-xs text-gray-500">{l.courriel}</div>}
                    </td>
                    <td className="px-6 py-3 text-right text-sm tabular-nums text-gray-900">
                      {euros(l.provisionCents)}
                      <div className="text-xs text-gray-500">
                        {l.mouvementCount} mouvement{l.mouvementCount > 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right text-sm tabular-nums text-gray-900">
                      {l.detteCents > 0 ? euros(l.detteCents) : '—'}
                      {l.facturesOuvertes > 0 && (
                        <div className="text-xs text-gray-500">
                          {l.facturesOuvertes} facture{l.facturesOuvertes > 1 ? 's' : ''} ouverte
                          {l.facturesOuvertes > 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td className={clsx(
                      'px-6 py-3 text-right text-sm tabular-nums',
                      l.aReaffecterCents > 0 ? 'font-semibold text-indigo-800' : 'text-gray-400',
                    )}>
                      {l.aReaffecterCents > 0 ? euros(l.aReaffecterCents) : '—'}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {dateCourte(l.dernierMouvement)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {/*
                        Le geste se fait dans l'écran de rapprochement, qui sait
                        défaire et refaire une affectation. On y mène, on ne le
                        réécrit pas ici.
                      */}
                      <Link
                        to={CHEMINS.comptabilite.rapprochement}
                        className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:text-indigo-900"
                        title="Ré-affecter dans l’écran de rapprochement"
                      >
                        Rapprochement <ArrowRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
